import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { LoadingContainer } from "../../components/LoadingContainer";
import type { StoreId } from "@KassaroAB/shared/types/ids";
import { useGetStoreQuery, useUpdateStoreMutation } from "../../query/storesQuery";
import { StoreForm } from "./StoreForm";

interface StoreInfoEditFormProps {
  onSuccess: (message: string) => void;
  onCancel?: () => void;
}

export const StoreInfoEditForm = ({ onSuccess, onCancel }: StoreInfoEditFormProps) => {
  const { t } = useTranslation();
  const { storeId: storeIdParam } = useParams<{ storeId: string }>();
  const storeId = storeIdParam as StoreId | undefined;
  const { data: store, isLoading, error: submitError } = useGetStoreQuery(storeId);
  const { mutate: updateStoreMutation, isPending, error: updateError } =
    useUpdateStoreMutation();

  const canEditOrgNumber = store?.activeCashRegister === null;
  const error = updateError || submitError;

  if (!storeId || isLoading || !store) {
    return <LoadingContainer message={t("overview.loadingStoreInfo")} />;
  }

  return (
    <StoreForm
      formKey={store.updatedAt}
      store={store}
      readOnlyOrgNumber={canEditOrgNumber ? undefined : store.orgNumber}
      errorMessage={error?.message}
      isLoading={isPending}
      submitLabel={t("overview.saveStore")}
      submitLoadingLabel={t("overview.savingStore")}
      onCancel={onCancel}
      onSubmit={(value) => {
        updateStoreMutation(
          {
            storeId,
            store: {
              name: value.name,
              displayName: value.displayName || undefined,
              street: value.street,
              postalcode: value.postalcode,
              city: value.city,
              orgNumber: canEditOrgNumber ? value.orgNumber : undefined,
              contactPerson: value.contactPerson,
              contactPhone: value.contactPhone,
              contactEmail: value.contactEmail,
              active: value.active,
            },
          },
          {
            onSuccess: () => {
              onSuccess(t("overview.storeSaved"));
            },
          },
        );
      }}
    />
  );
};
